import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let interval;
    const check = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(interval);
          setState("paid");
          if (data.booking_id) {
            try {
              const res = await api.get(`/bookings/${data.booking_id}`);
              setBooking(res.data);
            } catch (e) {
              /* summary is optional */
            }
          }
        } else if (attempts.current >= 10) {
          clearInterval(interval);
          setState("pending");
        }
      } catch (e) {
        if (attempts.current >= 10) {
          clearInterval(interval);
          setState("pending");
        }
      }
    };
    check();
    interval = setInterval(check, 2500);
    return () => clearInterval(interval);
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-[#090909] flex flex-col items-center justify-center px-6 py-24">
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>

        {state === "checking" && (
          <div data-testid="payment-checking-view">
            <Loader2 className="h-10 w-10 animate-spin text-[#D98B48] mx-auto" />
            <h1 className="font-display text-3xl mt-6 text-[#F5F5F5]">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-[#A3A3A3] font-light">Hang tight — this only takes a moment.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-paid-view">
            <div className="mx-auto h-16 w-16 rounded-full border border-[#D98B48]/40 bg-[#D98B48]/10 flex items-center justify-center">
              <Check className="h-8 w-8 text-[#D98B48]" />
            </div>
            <h1 className="font-display text-4xl mt-6 text-[#F5F5F5]">Deposit received.</h1>
            <p className="mt-3 text-sm text-[#A3A3A3] font-light">
              {booking
                ? `${booking.package_name} · ${booking.event_date} · ${booking.guest_count} guests. We'll confirm your booking within 24 hours.`
                : "Your date is reserved. We'll confirm your booking within 24 hours."}
            </p>
            <Button asChild data-testid="payment-success-home-button" className="mt-10 rounded-sm bg-[#D98B48] hover:bg-[#F0A562] text-[#090909] font-medium px-8 py-6">
              <Link to="/">Back to Estielle</Link>
            </Button>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="payment-pending-view">
            <Clock className="h-10 w-10 text-[#C5A059] mx-auto" />
            <h1 className="font-display text-3xl mt-6 text-[#F5F5F5]">Almost there.</h1>
            <p className="mt-3 text-sm text-[#A3A3A3] font-light">
              We haven't received the payment confirmation yet. If you completed
              checkout, your booking is safe — we'll email you once it clears.
            </p>
            <Button asChild variant="outline" className="mt-10 rounded-sm border-white/20 bg-transparent text-[#F5F5F5] hover:bg-white/10 px-8 py-6">
              <Link to="/">Back to Estielle</Link>
            </Button>
          </div>
        )}

        {state === "error" && (
          <div data-testid="payment-error-view">
            <h1 className="font-display text-3xl text-[#F5F5F5]">Missing payment reference.</h1>
            <p className="mt-3 text-sm text-[#A3A3A3] font-light">
              We couldn't find a payment session to confirm. If you think this is
              a mistake, contact us before booking again.
            </p>
            <Button asChild variant="outline" className="mt-10 rounded-sm border-white/20 bg-transparent text-[#F5F5F5] hover:bg-white/10 px-8 py-6">
              <Link to="/">Back to Estielle</Link>
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
